import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '@/styles/field-help-mark.css';

const EDGE = 8;
const MAX_WIDTH = 320;

/**
 * 表单 label 旁问号：悬浮显示要求说明（可换行，防溢出视口）。
 */
export default function FieldHelpMark({ text = '', className = '' }) {
  const triggerRef = useRef(null);
  const tipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const updatePos = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const maxWidth = Math.min(MAX_WIDTH, Math.max(180, window.innerWidth - EDGE * 2));
    let left = rect.left;
    if (left + maxWidth > window.innerWidth - EDGE) {
      left = Math.max(EDGE, window.innerWidth - maxWidth - EDGE);
    }
    if (left < EDGE) left = EDGE;
    const tipHeight = tipRef.current?.offsetHeight || 72;
    const belowTop = rect.bottom + 6;
    const top = belowTop + tipHeight > window.innerHeight - EDGE
      ? Math.max(EDGE, rect.top - tipHeight - 6)
      : belowTop;
    setPos({ top, left, maxWidth });
  }, []);

  const show = useCallback(() => {
    updatePos();
    setOpen(true);
  }, [updatePos]);

  const hide = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updatePos();
    const raf = window.requestAnimationFrame(() => updatePos());
    const onScrollOrResize = () => updatePos();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePos]);

  if (!text) return null;

  return (
    <>
      <span
        ref={triggerRef}
        className={`field-help-mark${className ? ` ${className}` : ''}`}
        aria-label={text}
        role="img"
        tabIndex={0}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        ?
      </span>
      {open && pos && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={tipRef}
            className="field-help-mark__tip"
            style={{ top: pos.top, left: pos.left, maxWidth: pos.maxWidth }}
            role="tooltip"
          >
            {text}
          </div>,
          document.body,
        )
        : null}
    </>
  );
}
